import React from 'react'
import { createPortal } from 'react-dom'
import { Modal } from 'react-bootstrap'
import FormGenerator from 'Presentation/Components/Form/FormGenerator'
import useViewModel from './ViewModel'
import formatNumberCommas from 'Utils/formatNumberCommas'
import { KTSVG } from '_metronic/helpers'
import clsx from 'clsx'
import moment from 'moment-timezone'

interface Props {
	hideBankTransfer?: boolean
	hideCreditCard?: boolean
	hidePromptPay?: boolean
	isOpen: boolean
	totalAmount: number
	onSubmit: (
		paymentType: 'bank_transfer' | 'credit-card' | 'promptpay',
		evidence?: File
	) => Promise<boolean>
	onClose: () => void
	disabled?: boolean
	promptPayData?: {
		qrImage: string | null
		qrExpireTime: string | null
		paymentId: string | null
	}
}

const PaymentMethod: React.FC<Props> = ({
	hideBankTransfer,
	hideCreditCard,
	hidePromptPay,
	isOpen,
	totalAmount,
	onSubmit,
	onClose,
	disabled,
	promptPayData,
}) => {
	const modalsRoot = document.getElementById('root-modals') || document.body
	const {
		step,
		formState,
		formError,
		heading,
		onSelectMethod,
		onClose: onCloseInternal,
		onUploadEvidence,
		onBackStep,
		submit,
	} = useViewModel({ isOpen, onSubmit })

	return createPortal(
		<Modal
			size="sm"
			id="kt_modal_payment_method"
			tabIndex={-1}
			aria-hidden="true"
			dialogClassName={clsx('transition-300 modal-dialog modal-dialog-centered', {
				'mw-600px': formState.paymentType === 'bank_transfer' && step === 2,
				'mw-400px': formState.paymentType !== 'bank_transfer' || step === 1,
			})}
			show={isOpen}
			backdrop={true}
			onHide={() => {
				onClose()
				onCloseInternal()
			}}>
			<div className="modal-content">
				<div className="modal-body">
					{/* Header */}
					<h1 className="text-kumopack-grey-900 text-center">{heading.title}</h1>
					<div className="text-kumupack-grey-600 text-center mb-5">{heading.description}</div>

					{/* BEGIN: METHOD SELECTION */}
					{step === 1 && (
						<React.Fragment>
							{!hideBankTransfer && (
								<div
									className="card border-kumopack-grey-200 shadow mb-5 cursor-pointer bg-kumopack-base-white hover-enabled transition-300"
									onClick={() => onSelectMethod('bank_transfer')}>
									<div className="card-body text-center">
										<h3 className="text-center mb-10">โอนเงินชำระ</h3>
										<img
											style={{ maxWidth: '100%' }}
											src="/media/logos/wallet.svg"
											alt="Bank Transfer"
											className="h-50px"
										/>
									</div>
								</div>
							)}

							{!hidePromptPay && (
								<div
									className="card border-kumopack-grey-200 shadow mb-5 cursor-pointer bg-kumopack-base-white hover-enabled transition-300"
									onClick={() => onSelectMethod('promptpay')}>
									<div className="card-body text-center">
										<h3 className="text-center mb-10">ชำระด้วยพร้อมเพย์</h3>
										<img
											style={{ maxWidth: '100%' }}
											src="/media/logos/prompt-pay-logo.png"
											alt="PromptPay"
											className="h-50px"
										/>
									</div>
								</div>
							)}

							{!hideCreditCard && (
								<div
									className="card border-kumopack-grey-200 shadow cursor-pointer bg-kumopack-base-white hover-enabled transition-300"
									onClick={() => onSelectMethod('credit-card')}>
									<div className="card-body text-center">
										<h3 className="text-center mb-10">บัตรเครดิต / บัตรเดบิต</h3>
										<img
											style={{ maxWidth: '100%' }}
											src="/media/logos/visa-master-jcb.webp"
											alt="Credit Card"
											className="h-50px"
										/>
									</div>
								</div>
							)}
						</React.Fragment>
					)}
					{/* END: METHOD SELECTION */}

					{/* BEGIN: EVIDENCE ATTACHMENT AND PAYMENT GATEWAY LOADING */}
					{step === 2 && (
						<React.Fragment>
							{formState.paymentType === 'bank_transfer' && (
								<React.Fragment>
									<div className="card border-grey-200 shadow mb-10">
										<div className="card-body">
											<div className="d-flex flex-column align-items-center justify-content-center">
												<div
													className="d-flex flex-row align-items-center"
													style={{ columnGap: '12px' }}>
													<div>
														<img
															src={`/media/thailand-bank-logo/KBANK.png`}
															alt="Bank Logo"
															width={35}
														/>
													</div>
													<div className="fs-5 text-kumopack-grey-700">
														<div className="fw-bold">บจก. คลาวน์แพ็คเกจจิ้ง</div>
														<div className="fw-semibold">1234567890</div>
													</div>
												</div>

												<div className="mt-10 text-center">
													<div className="fs-1 me-1">ยอดที่ต้องชำระ</div>
													<div className="fs-3x fw-bold">{formatNumberCommas(totalAmount)}</div>
												</div>
											</div>
										</div>
									</div>

									<div
										className={clsx(
											'w-100 rounded bg-kumopack-base-white border mb-5 text-center shadow d-flex align-items-center justify-content-center position-relative',
											{
												'border-kumopack-error-400': formError?.evidence,
											}
										)}
										style={{ height: '200px' }}>
										{formState.evidence && (
											<div
												className="btn btn-sm btn-icon btn-danger btn-active-light-danger position-absolute"
												style={{ top: -10, right: -10 }}
												onClick={() => onUploadEvidence(undefined)}>
												<KTSVG
													className="svg-icon-1"
													path="/media/icons/duotune/general/gen027.svg"
												/>
											</div>
										)}
										{formState.evidence && (
											<img
												className="mx-auto h-100 w-100 rounded"
												src={
													typeof formState.evidence !== 'string'
														? URL.createObjectURL(formState.evidence as any)
														: formState.evidence
												}
												onError={(e: any) => {
													e.target.onerror = null
													e.target.src = '/media/icons/kumopack/default-placeholder.png'
												}}
												style={{ objectFit: 'contain' }}
												alt="Factory Cover"
											/>
										)}
										{!formState.evidence && (
											<div className={'fw-bold fs-5 text-kumopack-grey-300 py-10'}>
												No Files Uploaded
											</div>
										)}
									</div>

									<FormGenerator
										formKey="admin-billing-form-drag-and-drop-evidence-picture"
										inputType="drag-and-drop"
										multiple={false}
										accept="image/png, image/jpeg, image/jpg"
										libAccept={{
											'image/png': [],
											'image/jpeg': [],
											'image/jpg': [],
										}}
										onFileUpload={coverFiles => onUploadEvidence(coverFiles[0] || null)}
									/>

									{formError?.evidence && (
										<div className="fv-plugins-message-container text-center mt-3 fs-3">
											<div className="fv-help-block">{formError?.evidence}</div>
										</div>
									)}
								</React.Fragment>
							)}

							{formState.paymentType === 'promptpay' && (
								<React.Fragment>
									<div className="card border-grey-200 shadow mb-10">
										<div className="card-body">
											<img
												src={`data:image/png;base64, ${promptPayData?.qrImage}`}
												alt="QR Code"
												style={{ maxWidth: '100%' }}
											/>

											<div className="fs-4 mt-3 text-kumopack-error-500 text-center fw-bold">
												Expired in{' '}
												{moment(promptPayData?.qrExpireTime)
													.tz('Asia/Bangkok')
													.format('DD/MM/YYYY HH:mm:ss')}
											</div>
										</div>
									</div>
								</React.Fragment>
							)}

							{formState.paymentType === 'credit-card' && (
								<React.Fragment>
									<div className="w-100 text-center py-5">
										<div
											className="spinner-border text-kumopack-primary-600 mx-auto"
											role="status"
											style={{
												width: '8em',
												height: '8em',
												borderWidth: 10,
											}}></div>
									</div>
								</React.Fragment>
							)}
						</React.Fragment>
					)}
					{/* END: EVIDENCE ATTACHMENT AND PAYMENT GATEWAY LOADING */}
				</div>

				{step !== 1 && formState.paymentType === 'bank_transfer' && (
					<div className="modal-footer">
						<div className="row w-100">
							<div className="col-12 col-xl-6">
								<button
									disabled={disabled}
									className="btn btn-sm white-button text-kumopack-grey-700 fw-bold w-100"
									onClick={onBackStep}>
									<span>ย้อนกลับ</span>
								</button>
							</div>
							<div className="col-12 col-xl-6">
								<button
									disabled={disabled}
									className="btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white fw-bold w-100"
									onClick={submit}>
									<span>ยืนยัน</span>
								</button>
							</div>
						</div>
					</div>
				)}
			</div>
		</Modal>,
		modalsRoot
	)
}

export default PaymentMethod
